import React, { useEffect, useState } from "react";
import { ModalX } from "../../components";
import { Button, Checkbox, Input, Option, Select } from "@material-tailwind/react";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { LEVELS, LEVELS_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM, SECURITY_TYPES, SECURITY_TYPES_ENUM } from "../../utils/enums";
import { useAddOffice, useUpdateOffice } from "../../custom-hooks/use-offices";
import { useGetFloors } from "../../custom-hooks/use-floors";

const INITIAL_FORM_DATA = {
    floor: '',
    number: '',
    code: '',
    capacity: '',
    useableArea: '',
    totalArea: '',
    status: '',
    securityType: '',
    securityLevel: '',
    isAccessible: true,
    isBookable: true,
    dedicatedClimateControl: true,
    dedicatedInternet: false,
    hourlyRate: 0,
    dailyRate: 0,
    monthlyRate: 0,
    currency: 'PKR',
};

/**
 * Self-contained create/update modal for offices.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} [props.office] - null/undefined for create, office object for edit
 * @param {string} [props.defaultFloorId] - optional floor id to preselect (and lock) on create
 * @param {() => void} [props.onSuccess] - called after successful add/update
 */
const OfficeCrudModal = ({
    isOpen,
    onClose,
    office = null,
    defaultFloorId,
    onSuccess,
}) => {
    const isUpdating = Boolean(office);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    const addOfficeMutation = useAddOffice();
    const updateOfficeMutation = useUpdateOffice();
    const { data: floors, isLoading: isFloorsLoading, isError: isFloorsError } = useGetFloors();

    const floorLocked = Boolean(defaultFloorId) && !isUpdating;

    useEffect(() => {
        if (!isOpen) return;

        if (office) {
            setFormData({ ...office, floor: office.floor?._id || office.floor || '' });
        } else {
            const defaults = { ...INITIAL_FORM_DATA };
            defaults.status = OPERATIONAL_STATUSES.OPERATIONAL;
            defaults.securityType = SECURITY_TYPES.SHARED_FINGERPRINT;
            defaults.securityLevel = LEVELS.MEDIUM;
            if (defaultFloorId) {
                defaults.floor = defaultFloorId;
            }
            setFormData(defaults);
        }
    }, [isOpen, office, defaultFloorId]);

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckBoxChange = async (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleClose = () => {
        setFormData(INITIAL_FORM_DATA);
        onClose();
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                floor: formData.floor,
                number: formData.number,
                code: formData.code,
                capacity: formData.capacity,
                useableArea: formData.useableArea,
                totalArea: formData.totalArea,
                status: formData.status,
                securityType: formData.securityType,
                securityLevel: formData.securityLevel,
                isAccessible: formData.isAccessible,
                isBookable: formData.isBookable,
                dedicatedClimateControl: formData.dedicatedClimateControl,
                dedicatedInternet: formData.dedicatedInternet,
                hourlyRate: formData.hourlyRate,
                dailyRate: formData.dailyRate,
                monthlyRate: formData.monthlyRate,
            };

            if (isUpdating) {
                await updateOfficeMutation.mutateAsync({ officeId: office._id, data: payload });
            } else {
                await addOfficeMutation.mutateAsync({ floorId: formData.floor, data: payload });
            }

            onSuccess?.();
        } catch (error) {
            console.log('error', error);
            NotifyError(error.response.data.message, error);
        } finally {
            setFormData(INITIAL_FORM_DATA);
            onClose();
        }
    };

    return (
        <ModalX
            isOpen={isOpen}
            onClose={handleClose}
            title={isUpdating ? "Update Office" : "Add New Office"}
            maxWidth="xxl"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-2">
                    <div>
                        {isFloorsError && <div className="text-center py-5 text-red-500">Error loading floors. Please try again.</div>}
                        <Select
                            label={<>Select Floor <span className="text-red-500 ml-1">*</span></>}
                            value={formData.floor}
                            onChange={(newValue) => handleSelectChange("floor", newValue)}
                            disabled={isFloorsLoading || floorLocked}
                        >
                            {floors?.map((floor, index) => (
                                <Option key={index} value={floor._id}>
                                    <div className="flex justify-between gap-3 w-full">
                                        <div className="">
                                            {getOrdinal(floor.number)} Floor - {floor.floorCode} in ({floor.building.name})
                                        </div>
                                        <div className="">
                                            {floor.building.branch.name}
                                        </div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <Input
                            label="Office Number"
                            name="number"
                            value={formData.number}
                            onChange={handleInputChange}
                            required
                        />
                        <Input
                            label="Office Code"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className={`mt-5`}>
                        <span className="font-thin text-lg">Specifications</span>
                    </div>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <Input
                            label="Person Capacity"
                            name="capacity"
                            type="number"
                            required
                            value={formData.capacity}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Usable Area (sq/ft)"
                            name="useableArea"
                            type="number"
                            value={formData.useableArea}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Total Area (sq/ft)"
                            name="totalArea"
                            type="number"
                            value={formData.totalArea}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <Select
                            label="Status"
                            value={formData.status}
                            onChange={newValue => handleSelectChange("status", newValue)}
                        >
                            {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                                <Option key={index} value={status}>
                                    {convertToPresentableText(status)}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            label="Security Level"
                            value={formData.securityLevel}
                            onChange={newValue => handleSelectChange("securityLevel", newValue)}
                        >
                            {LEVELS_ENUM.map((level, index) => (
                                <Option key={index} value={level}>
                                    {convertToPresentableText(level)}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            label="Security Type"
                            value={formData.securityType}
                            onChange={newValue => handleSelectChange("securityType", newValue)}
                        >
                            {SECURITY_TYPES_ENUM.map((type, index) => (
                                <Option key={index} value={type}>
                                    {convertToPresentableText(type)}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="">
                        <Checkbox
                            name="isBookable"
                            checked={formData.isBookable}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Is Bookable"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="isAccessible"
                            checked={formData.isAccessible}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Is Accessible"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="dedicatedInternet"
                            checked={formData.dedicatedInternet}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Dedicated Internet"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="dedicatedClimateControl"
                            checked={formData.dedicatedClimateControl}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Dedicated Climate Control"
                            onChange={handleCheckBoxChange}
                        />
                    </div>
                    <div className={`mt-5`}>
                        <span className="font-thin text-lg">Pricing</span>
                    </div>
                    <div className={`flex gap-4`}>
                        <Input
                            label="Hourly Rent"
                            name="hourlyRate"
                            type="number"
                            value={formData.hourlyRate}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Daily Rent"
                            name="dailyRate"
                            type="number"
                            value={formData.dailyRate}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Monthly Rent"
                            name="monthlyRate"
                            type="number"
                            value={formData.monthlyRate}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                    <Button
                        onClick={handleClose}
                        className="rounded-full "
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="rounded-full"
                        disabled={addOfficeMutation.isLoading || updateOfficeMutation.isLoading}
                        loading={addOfficeMutation.isLoading || updateOfficeMutation.isLoading}
                    >
                        {isUpdating ? "Update" : "Add"}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default OfficeCrudModal;
export { OfficeCrudModal, INITIAL_FORM_DATA };
